import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

/**
 * Кто и когда сохранил/подтвердил день в `studio_daily_totals`.
 *
 * Читается только через RPC `get_studio_daily_authorship`: времени сохранения
 * в таблице нет (`updated_at` двигает ночной пересчёт активных посетителей),
 * а `gross_revenue_confirmed_by` хранит и uuid, и легаси-telegram_id — резолвить
 * это во фронте нечем. Обе даты RPC отдаёт по серверным часам, поэтому их можно
 * показывать рядом (колонка `gross_revenue_confirmed_at` пишется браузером и
 * расходится с серверным временем на десятки секунд).
 */
export interface StudioDailyAuthorship {
  savedByUserId: string | null
  savedByName: string | null
  savedAt: string | null
  confirmed: boolean
  confirmedByUserId: string | null
  confirmedByName: string | null
  /** Исходное значение `gross_revenue_confirmed_by`, если имя не резолвится. */
  confirmedByRaw: string | null
  confirmedAt: string | null
}

function mapRow(row: Record<string, any>): StudioDailyAuthorship {
  return {
    savedByUserId: row.saved_by_user_id ?? null,
    savedByName: row.saved_by_name ?? null,
    savedAt: row.saved_at ?? null,
    confirmed: row.confirmed === true,
    confirmedByUserId: row.confirmed_by_user_id ?? null,
    confirmedByName: row.confirmed_by_name ?? null,
    confirmedByRaw: row.confirmed_by_raw ?? null,
    confirmedAt: row.confirmed_at ?? null,
  }
}

/**
 * Возвращает `null`, если авторства нет (день не заполнялся человеком), нет
 * доступа к студии или RPC ещё не применена на окружении: подпись авторства —
 * справочная, она не должна ломать экран ввода показателей.
 */
export async function loadStudioDailyAuthorship(
  studioInternalId: number,
  metricDate: string
): Promise<StudioDailyAuthorship | null> {
  try {
    const supabase = getSupabase()
    const { data, error } = await withAuthRecovery(() =>
      (supabase as any).rpc('get_studio_daily_authorship', {
        p_studio_internal_id: studioInternalId,
        p_metric_date: metricDate,
      })
    )

    if (error) {
      console.warn('loadStudioDailyAuthorship failed:', error)
      return null
    }

    const rows = Array.isArray(data) ? data : data ? [data] : []
    if (rows.length === 0) return null

    return mapRow(rows[0] as Record<string, any>)
  } catch (err) {
    console.warn('loadStudioDailyAuthorship failed:', err)
    return null
  }
}
